import { plainToInstance } from "class-transformer";
import { inject, injectable } from "inversify";
import 'reflect-metadata';

import { ArchiwebServicesClient, Archiweb_Services_Client_DIID, CredentialLoginResponse, Credentials, TrustedTimeService, Trusted_Time_Service_DIID } from '@archipad-js/archipad/archiweb-services-client';
import { validateOrRejectSync } from "@archipad-js/core/validation";
import { DIID } from "@archipad-js/dependency-injection";
import _LegacyServiceManager from "@core/services/legacy-service-manager";
import { callAsAsync } from "@core/tasks/asyncCaller";
import { ProgressObserver, makeProgressHandler } from '@core/tasks/progress';

import { hashString } from "./login.service.helper";
import { LoginParameters } from './login.service.types';

export const Login_Service_DIID = DIID<LoginService>("LoginService");

/**
 * [PLUS-158] Delete this service and all is usages
 */
@injectable()
export class LoginService {
    constructor(
        @inject(Archiweb_Services_Client_DIID.symbol) private readonly archiwebServicesClient:ArchiwebServicesClient,
        @inject(Trusted_Time_Service_DIID.symbol) private readonly trustedTimeService:TrustedTimeService,
    ) {}

    /**
     * Hashes {@link Credentials.login} and {@link Credentials.hashedPassword}
     * from the supplied {@link Credentials} and returns {@link LoginParameters}
     * using {@link TrustedTimeService.trustedTime} required by `credential/login` API.
     * 
     * @param credentials 
     * @returns LoginParameters
     */
    public getLoginParametersFromCredentials(credentials:Credentials):LoginParameters {
        const requestTimestamp = this.trustedTimeService.trustedTime;
        const auth = credentials.login + (credentials.hashedPassword ? credentials.hashedPassword : hashString(credentials.password) ) + requestTimestamp;
        const authHash = hashString(auth);
    
        const loginInfo = {
            "login" : credentials.login,
            "hash" : authHash,
            "timestamp" : requestTimestamp,
        };
    
        return loginInfo;
    }

    /**
     * Handles the __unauthenticated__ call to `credential/login` API using {@link this.archipadRpcService}
     * 
     * @param signal 
     * @param progress 
     * @param credentials 
     * @returns 
     */
    public async login(signal:AbortSignal, progress:ProgressObserver, credentials:Credentials):Promise<CredentialLoginResponse> {
        const p = makeProgressHandler(progress);
        p.total(1);

        const params = this.getLoginParametersFromCredentials(credentials);
        const loginResponse = await callAsAsync(signal, p, 1, (po) => this.archiwebServicesClient.makeUnauthenticatedRequest(signal, "credential", "login", params) );

        const loginObject = plainToInstance(CredentialLoginResponse, loginResponse);
        validateOrRejectSync(loginObject);
        return loginObject;
    }

    public hashString(password:string):string {
        return hashString(password);
    }
}

export function getLoginService():LoginService {
    return _LegacyServiceManager.container.get(Login_Service_DIID.symbol);
}